/**
 * What a function needs before it can run, and whether you have it yet.
 *
 * Running something and finding out afterwards that a prerequisite was missing
 * is the expensive order to discover it in — a pipeline stage can be minutes
 * in before it fails on an absent file. So the requirements are worked out
 * first, shown as a short checklist, and the ones that are not met are the
 * things the sheet asks for.
 *
 * Pure, so the rules can be tested without mounting a dialog. The UI in
 * `LaunchSheet.tsx` only renders what this returns.
 */

import type { CaseManifest, EngineStatus } from "@/ipc/contract";
import type { ToolDef } from "@/tools/catalog";
import type { UseCase } from "@/usecases/catalog";

export type RequirementId = "study" | "engine" | "volume" | "reference" | "outputs";

export interface Requirement {
  id: RequirementId;
  label: string;
  /** Met, or the reason it is not. */
  met: boolean;
  /** What satisfies it now, when it is met. */
  detail?: string;
  /** The button that would satisfy it, when it is not. */
  fix?: { label: string; action: string };
}

export interface LaunchContext {
  manifest: CaseManifest | null;
  engine: EngineStatus | null | undefined;
  /** The volume currently selected in the Study Explorer. */
  selectedVolumeLabel: string | null;
  /** A second volume, for the tools that compare two. */
  referenceLabel: string | null;
  hasResults: boolean;
}

/** The subject of the sheet: either a catalog tool or a use case. */
export interface LaunchTarget {
  title: string;
  /** One or two sentences: what this does, in outcomes. */
  summary: string;
  /** Free-text prerequisites the catalog already states. */
  needs?: string[];
  /** True for anything that runs the pipeline or a Python op. */
  needsEngine: boolean;
  /** True for an image operation, which acts on the selected volume. */
  needsVolume: boolean;
  /** True for a two-image operation. */
  needsReference: boolean;
  /** True for anything that reads a finished run. */
  needsOutputs: boolean;
  /** Set when the capability is not built; the sheet explains and offers no run. */
  unavailable?: string;
}

export function targetFromTool(tool: ToolDef): LaunchTarget {
  const isStage = Boolean(tool.stage);
  return {
    title: tool.label,
    summary: tool.description,
    needsEngine: isStage || Boolean(tool.op),
    // A pipeline stage takes its inputs from the case, not from whatever
    // happens to be highlighted; only image tools act on the selection.
    needsVolume: !isStage && Boolean(tool.op),
    needsReference: Boolean(tool.needsReference),
    needsOutputs: false,
    unavailable: tool.status === "future" ? (tool.comingSoon ?? "Not built yet.") : undefined,
  };
}

export function targetFromUseCase(useCase: UseCase): LaunchTarget {
  const kind = useCase.action.kind;
  return {
    title: useCase.title,
    summary: useCase.outcome,
    needs: useCase.needs,
    needsEngine: kind === "pipeline" || kind === "batch",
    needsVolume: false,
    needsReference: false,
    needsOutputs: useCase.id === "read.review" || useCase.id === "data.export",
    unavailable: useCase.unavailable,
  };
}

export function requirementsFor(target: LaunchTarget, ctx: LaunchContext): Requirement[] {
  const out: Requirement[] = [];

  out.push({
    id: "study",
    label: "A study is open",
    met: ctx.manifest != null,
    detail: ctx.manifest?.subjectId,
    fix: { label: "Open a study…", action: "case.open" },
  });

  if (target.needsEngine) {
    const ready = ctx.engine?.ready === true;
    out.push({
      id: "engine",
      label: "A processing engine is ready",
      met: ready,
      detail: ready ? ctx.engine?.deviceLabel : undefined,
      fix: { label: "Set up the engine…", action: "dev.engine.setup" },
    });
  }

  if (target.needsVolume) {
    out.push({
      id: "volume",
      label: "An image is selected",
      met: ctx.selectedVolumeLabel != null,
      detail: ctx.selectedVolumeLabel ?? undefined,
      fix: { label: "Choose in the Study Explorer", action: "dock.show.explorer" },
    });
  }

  if (target.needsReference) {
    out.push({
      id: "reference",
      label: "A second image to compare against",
      met: ctx.referenceLabel != null,
      detail: ctx.referenceLabel ?? undefined,
      fix: { label: "Choose in the panel", action: "dock.show.explorer" },
    });
  }

  if (target.needsOutputs) {
    out.push({
      id: "outputs",
      label: "The run has produced results",
      met: ctx.hasResults,
      fix: { label: "Run the analysis first", action: "goto.dwi" },
    });
  }

  return out;
}

/** Null when everything is in place, otherwise why the primary button is off. */
export function blockingReason(target: LaunchTarget, reqs: Requirement[]): string | null {
  if (target.unavailable) return target.unavailable;
  const missing = reqs.filter((r) => !r.met);
  if (missing.length === 0) return null;
  if (missing.length === 1) return missing[0].label;
  return `${missing.length} things are still missing`;
}
